import { useState } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Formik } from 'formik';
import { Button, Form } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import images from '../../assets/images';
import OTPField from '../../components/common/OTPField';
import { ISignUpOTPFormField } from '../../models/login';
import type { IUserData } from '../../models/profile';
import type { StandardResponse } from '../../models/standard-response';
import { login, resendOtp, signupConfirm } from '../../services/login/login-service';
import { getProfile } from '../../services/user/user-service';
import { AppDispatch } from '../../store';
import {
  changeLanguage,
  saveUserData,
  setCurrencyDetails,
  userDetail as setUserDetail,
} from '../../store/actions/app.Actions';
import { ISignUpOTPProps } from '../../types/propTypes';
import { companyNameReplace } from '../../utils';
import { Common } from '../../utils/constants';
import { maskEmail } from '../../utils/helper';
import { useCountdownTimer } from '../../utils/useCountdownTimer';
import { SignUpOTPSchema } from '../../validation/login/login-validation';

const SignUpOTPForm = (props: ISignUpOTPProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const initialState: ISignUpOTPFormField = {
    code: '',
  };

  const { secondsLeft, start, formattedTime, isRunning } = useCountdownTimer({
    initialSeconds: 45,
    autoStart: true,
  });

  const [isSubmittingOTP, setIsSubmittingOTP] = useState<boolean>(false);

  const handleSubmit = async (value: ISignUpOTPFormField) => {
    value.username = props.email.toLowerCase();
    sessionStorage.setItem(Common.OTP_VERIFICATION_IN_PROGRESS, 'true');
    setIsSubmittingOTP(true);
    document.getElementById('loading')?.classList.add('manual-loading');

    return signupConfirm(value)
      .then(async (res) => {
        if (res.isSignUpComplete === true) {
          try {
            const storedCredentials = sessionStorage.getItem(Common.SIGNUP_CREDENTIALS);

            let email: string = props.email;
            let password: string = '';

            if (storedCredentials) {
              const parsed = JSON.parse(storedCredentials);
              email = parsed.email;
              password = parsed.password;
            }

            let captchaToken = '';

            await login({ email, password, recaptchaCode: captchaToken, rememberMe: false }, false, true);

            const currentUser = await getCurrentUser();
            const authSession = await fetchAuthSession();
            const userDetail: StandardResponse<IUserData> = await getProfile();

            dispatch(saveUserData({ ...currentUser, authSession, ...userDetail.Data }));
            dispatch(changeLanguage(userDetail?.Data?.language_preference));
            dispatch(setCurrencyDetails(userDetail?.Data?.default_currency));
            dispatch(setUserDetail(userDetail?.Data));

            sessionStorage.removeItem(Common.SIGNUP_CREDENTIALS);

            sessionStorage.setItem(Common.SIGNUP_FLOW_ACTIVE, 'true');

            localStorage.setItem(Common.SHOW_REFERRAL_SCREEN, 'true');

            sessionStorage.removeItem(Common.OTP_VERIFICATION_IN_PROGRESS);

            navigate('/auth/select-auth-option', { replace: true });
          } catch (error) {
            sessionStorage.removeItem(Common.OTP_VERIFICATION_IN_PROGRESS);
            sessionStorage.removeItem(Common.SIGNUP_CREDENTIALS);
            sessionStorage.removeItem(Common.SIGNUP_FLOW_ACTIVE);
            navigate('/login');
          }
        }
      })
      .catch((err) => {
        sessionStorage.removeItem(Common.OTP_VERIFICATION_IN_PROGRESS);
        if (err && err.name === Common.Authentication.CodeMismatchException) {
          toast.error(t('signup.toast.invalidCode'));
        } else {
          toast.error(t('signup.toast.error'));
        }
      })
      .finally(() => {
        sessionStorage.removeItem(Common.OTP_VERIFICATION_IN_PROGRESS);
        setIsSubmittingOTP(false);
        document.getElementById('loading')?.classList.remove('manual-loading');
      });
  };

  const resendOTPSubmit = async (resetForm: () => void, setFieldTouched: (field: string, touched: boolean) => void) => {
    resetForm();
    setFieldTouched('code', false);
    await resendOtp(props.email)
      .then(() => {
        toast.success(t('signup.toast.resendSignupCode'));
        start();
      })
      .catch((error) => {
        if (error && error.name === 'LimitExceededException') {
          toast.error(t('signup.toast.resendLimitExceeded'));
        } else {
          toast.error(t('signup.toast.error'));
        }
      });
  };

  return (
    <div className="font-poppins">
      <Helmet>
        <title>{companyNameReplace(t('signup.title'))} </title>
      </Helmet>
      <button
        type="button"
        className="btn btn-icon btn-link pr-0 btn-back"
        onClick={() => props.onChangeStage(Common.AuthStage.SIGNIN)}
        title={t<string>('signup.button.back')}
      >
        <img className="icon" src={images.backArrowRSVG} alt={t<string>('signup.button.back')} />
      </button>
      <div className="title-outer">
        <div className="d-flex justify-content-center align-items-center">
          <div className="sign-up-icon-container">
            <img className="sign-up-icon" src={images.OTPEmailIcon} alt={t<string>('navbar.title')} />
          </div>
        </div>
        <h1 className="title sign-up-title mt-5 mb-3">{t('signup.label.signUpOTPConfirmationTitle')}</h1>

        <span className="sign-up-otp-message">{t('signup.label.signUpOTPConfirmationMessage')}</span>
        <p className="sign-up-otp-message mb-4">{maskEmail(props.email)}</p>
      </div>
      <Formik initialValues={initialState} onSubmit={handleSubmit} validationSchema={SignUpOTPSchema(t)}>
        {(props) => {
          const { handleSubmit, resetForm, setFieldTouched } = props;
          return (
            <form
              className="form-secondary"
              onSubmit={handleSubmit}
              autoComplete="off"
              noValidate
              onKeyDown={(event) => {
                if (event.key === 'Enter' && isSubmittingOTP) {
                  event.preventDefault();
                }
              }}
            >
              <div className="app-setup-otp-container p-1 mb-3">
                <OTPField name="code" customStyleClass="app-setup-otp" />
              </div>

              <Form.Group className="form-group mb-5 mt-4">
                <Button
                  type="submit"
                  className="btn btn-primary w-100 sign-up-otp-submit"
                  disabled={sessionStorage.getItem(Common.OTP_VERIFICATION_IN_PROGRESS) === 'true' || isSubmittingOTP}
                >
                  {t('signup.button.confirm')}
                </Button>
              </Form.Group>

              {isRunning && secondsLeft > 0 ? (
                <div className="clearfix pt-1">
                  <p className="sign-up-otp pb-0 mb-2">{t('signup.label.otpSentMessage')}</p>
                  <p className="sign-up-otp">
                    <span>
                      {t('signup.label.resendMessage')} {formattedTime}
                    </span>
                  </p>
                </div>
              ) : (
                <div className="clearfix pt-1">
                  <p className="sign-up-otp pb-0 mb-2">{t('signup.label.resendTitle')} </p>
                  <p className="sign-up-otp">
                    <a
                      href="#"
                      title={t<string>('signup.button.resend')}
                      onClick={(event) => {
                        event.preventDefault();
                        resendOTPSubmit(resetForm, setFieldTouched);
                      }}
                    >
                      {t('signup.button.resend')}
                    </a>
                  </p>
                </div>
              )}
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default SignUpOTPForm;
